import usePlatformMode from '@Hooks/usePlatformMode';
import { Accordion, AccordionDetails, AccordionSummary, Icon, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
	BundlesStatsTab,
	ClassroomsStatsTab,
	StandaloneCoursesStatsTab,
} from './FamilyTabs';
import OverviewTab from './OverviewTab';

/**
 * Admin drill-down into one instructor: the Overview scoped to them (wallet
 * block never renders under an instructor filter) plus lazy per-family
 * sections. This is the same view the instructor portal itself gets — the
 * backend scope is identical, only the source of the instructor id differs.
 */
const InstructorDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const location = useLocation<{ name?: string }>();
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled } = usePlatformMode();
	const instructorId = Number(id);
	const name = location.state?.name || `Instructor #${id}`;

	const families = [
		{ key: 'classrooms', label: 'Classrooms', enabled: classroomsEnabled, component: <ClassroomsStatsTab instructorId={instructorId} /> },
		{ key: 'standalone-courses', label: 'Standalone Courses', enabled: standaloneEnabled, component: <StandaloneCoursesStatsTab instructorId={instructorId} /> },
		{ key: 'bundles', label: 'Bundles', enabled: bundlesEnabled, component: <BundlesStatsTab instructorId={instructorId} /> },
	];

	return (
		<DetailPage>
			<Typography variant="h5" mb="1.5rem">
				{name}
			</Typography>
			<OverviewTab instructorId={instructorId} />
			{families
				.filter((family) => family.enabled)
				.map((family) => (
					// Lazy mount: each family fires its own set of queries, so only
					// the expanded one loads.
					<Accordion key={family.key} TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}>
						<AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
							<Typography fontWeight={600}>{family.label}</Typography>
						</AccordionSummary>
						<AccordionDetails>{family.component}</AccordionDetails>
					</Accordion>
				))}
		</DetailPage>
	);
};

const DetailPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
	padding-bottom: 3rem;
`;

export default InstructorDetail;
